import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { chimneyService } from '../services/chimneyService';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const ChimneysList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cityFromUrl = searchParams.get('city') || '';

  const [chimneys, setChimneys] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [visiblePhones, setVisiblePhones] = useState({});
  const [cityInput, setCityInput] = useState(cityFromUrl);

  const loadPage = useCallback(async (page = 1, append = false, city = cityFromUrl) => {
    const setBusy = append ? setLoadingMore : setLoading;
    try {
      setBusy(true);
      setError('');
      const data = await chimneyService.getPage(page, { city });
      const list = data.results || [];
      setChimneys((prev) => (append ? [...prev, ...list] : list));
      setTotalCount(data.count ?? 0);
      setNextPage(data.next ? page + 1 : null);
    } catch (err) {
      setError('Nie udało się załadować listy kominiarzy');
      console.error('Błąd:', err);
      if (!append) {
        setChimneys([]);
        setTotalCount(0);
        setNextPage(null);
      }
    } finally {
      setBusy(false);
    }
  }, [cityFromUrl]);

  useEffect(() => {
    loadPage(1, false, cityFromUrl);
  }, [cityFromUrl, loadPage]);

  useEffect(() => {
    setCityInput(cityFromUrl);
  }, [cityFromUrl]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = cityInput.trim();
    if (q) {
      setSearchParams({ city: q });
    } else {
      setSearchParams({});
    }
  };

  const handleLoadMore = () => {
    if (nextPage && !loadingMore) loadPage(nextPage, true, cityFromUrl);
  };

  const togglePhoneVisibility = (id) => {
    setVisiblePhones((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="ik-container py-16">
          <p className="text-center font-display text-lg text-ember-600">Ładowanie…</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="ik-container py-16">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800">
            {error}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Lista kominiarzy w Polsce | iKominiarz.pl"
        description="Przeglądaj listę zweryfikowanych kominiarzy z całej Polski. Znajdź profesjonalnego specjalistę w swojej okolicy, przeczytaj opinie klientów i umów wizytę online."
        keywords="lista kominiarzy, kominiarze w Polsce, znaleźć kominiarza, kominiarz online, przegląd kominiarzy"
        url="https://ikominiarz.pl/chimneys"
      />

      <div className="border-b border-soot-200 bg-soot-900 text-white">
        <div className="ik-container py-12 md:py-16">
          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Kominiarze
          </h1>
          <p className="mt-3 max-w-xl text-soot-300">
            Wybierz fachowca, zobacz profil i umów przegląd online.
          </p>
          <form onSubmit={handleSearch} className="mt-8 flex max-w-xl flex-col gap-3 sm:flex-row">
            <label className="sr-only" htmlFor="list-city">
              Filtruj po mieście
            </label>
            <input
              id="list-city"
              type="text"
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              placeholder="Miasto lub kod pocztowy"
              className="w-full rounded-md border-0 bg-white px-4 py-3 text-soot-900 placeholder:text-soot-400 focus:outline-none focus:ring-2 focus:ring-ember-500"
            />
            <button type="submit" className="ik-btn-primary whitespace-nowrap">
              Filtruj
            </button>
          </form>
        </div>
      </div>

      <div className="ik-container py-10 md:py-14">
        <div className="mb-8 flex flex-wrap items-baseline justify-between gap-3">
          <p className="text-sm text-soot-600">
            {cityFromUrl
              ? totalCount > 0
                ? `Wyniki dla „${cityFromUrl}”: wyświetlono ${chimneys.length} z ${totalCount}`
                : `Brak wyników dla „${cityFromUrl}”`
              : totalCount > 0
                ? `Wyświetlono ${chimneys.length} z ${totalCount}`
                : 'Brak wyników'}
          </p>
          {cityFromUrl && (
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="text-sm font-medium text-ember-600 hover:text-ember-700"
            >
              Wyczyść filtr
            </button>
          )}
        </div>

        {!Array.isArray(chimneys) || chimneys.length === 0 ? (
          <div className="border-t border-soot-200 py-16 text-center">
            <p className="text-lg text-soot-600">
              {cityFromUrl
                ? 'Brak kominiarzy pasujących do tego filtra.'
                : 'Brak dostępnych kominiarzy w systemie.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-soot-200 border-t border-soot-200">
            {chimneys.map((chimney) => (
              <li key={chimney.id}>
                <Link
                  to={`/chimney/${chimney.id}`}
                  className="group flex flex-col gap-4 py-6 transition sm:flex-row sm:items-center sm:justify-between sm:gap-8"
                >
                  <div className="min-w-0 flex-1">
                    <h2 className="font-display text-xl font-semibold text-soot-950 transition group-hover:text-ember-600">
                      {chimney.company_name || 'Kominiarz'}
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-soot-600">
                      {chimney.city && <span>{chimney.city}</span>}
                      {chimney.postal_code && <span>{chimney.postal_code}</span>}
                      {chimney.phone && (
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            togglePhoneVisibility(chimney.id);
                          }}
                          className="cursor-pointer font-medium text-ember-600 hover:underline"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              togglePhoneVisibility(chimney.id);
                            }
                          }}
                        >
                          {visiblePhones[chimney.id] ? chimney.phone : 'Pokaż numer'}
                        </span>
                      )}
                    </div>
                    {chimney.description && (
                      <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-soot-500">
                        {chimney.description}
                      </p>
                    )}
                  </div>
                  <span className="ik-btn-secondary shrink-0 !px-4 !py-2 text-sm group-hover:border-soot-900">
                    Zobacz profil
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {nextPage && (
          <div className="mt-10 flex justify-center border-t border-soot-200 pt-10">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="ik-btn-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingMore ? 'Ładowanie…' : 'Załaduj więcej'}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ChimneysList;
